// API Configuration
// Use REACT_APP_PRODUCT_API_URL for product API, fallback to localhost:5000
const API_BASE_URL = process.env.REACT_APP_PRODUCT_API_URL || 'http://localhost:5001/api';

// Types for API responses
export interface Product {
  id?: string;
  photo: string | null;
  name: string;
  desc: string | null;
  key_feature: string | null;
  why_choose_our_solution: string | null;
}

export interface Subcategory {
  id: string;
  categoriesName: string;
  video?: string | null;
  shortDescription?: string | null;
  briefDescription?: string | null;
  products: Product[];
}

export interface Category {
  id: string;
  title: 'software' | 'product and service';
  subcategories: Subcategory[];
}

// Fetch all categories, subcategories, and products
export const fetchAllCategories = async (): Promise<Category[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/category/all`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: Category[] = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

// Helper to get software category data
export const getSoftwareCategory = (categories: Category[]): Category | undefined => {
  return categories.find(cat => cat.title === 'software');
};

// Helper to get product and service category data
export const getProductServiceCategory = (categories: Category[]): Category | undefined => {
  return categories.find(cat => cat.title === 'product and service');
};

// Generate slug from name
export const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

// Generate translation key from product/category name
export const getProductTranslationKey = (name: string): string => {
  const words = name.split(/\s+/);
  const camelCase = words
    .map((word, index) => {
      if (index === 0) return word.toLowerCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('');
  return `dynamicContent.${camelCase}`;
};

// Find subcategory by slug
export const findSubcategoryBySlug = (
  categories: Category[],
  categoryType: 'software' | 'product and service',
  slug: string
): Subcategory | undefined => {
  const category = categories.find(cat => cat.title === categoryType);
  if (!category) return undefined;

  return category.subcategories.find(
    sub => generateSlug(sub.categoriesName) === slug
  );
};

// Find product by name slug within a subcategory
export const findProductBySlug = (
  subcategory: Subcategory,
  productSlug: string
): Product | undefined => {
  return subcategory.products.find(
    product => generateSlug(product.name) === productSlug
  );
};

// Get full URL for uploaded file from backend
export const getFileUrl = (filePath: string | null | undefined): string | null => {
  if (!filePath) return null;

  // If it's already a full URL, return as is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }

  // Get API base URL (remove /api suffix if present)
  const baseUrl = API_BASE_URL.replace('/api', '');

  // Ensure path starts with /uploads
  const normalizedPath = filePath.startsWith('/uploads')
    ? filePath
    : filePath.startsWith('uploads')
      ? `/${filePath}`
      : `/uploads/${filePath}`;

  return `${baseUrl}${normalizedPath}`;
};

